import React, { useEffect, useMemo, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Cell,
} from 'recharts';
import { LineChart as LineIcon, TrendingUp, TrendingDown, Wallet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};
const fmtN = (n, dp = 0) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: dp }));
const pct = (n, dp = 2) => (n == null ? '—' : `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(dp)}%`);

const StatCard = ({ icon: Icon, label, value, tone = 'default', sub }) => {
  const toneMap = {
    default: 'text-white',
    positive: 'text-emerald-400',
    negative: 'text-red-400',
    warning: 'text-amber-400',
  };
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 uppercase tracking-wider">
        {Icon && <Icon className="w-3 h-3" />}
        <span>{label}</span>
      </div>
      <p className={`text-lg font-bold mt-1 ${toneMap[tone]}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const TooltipCard = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-gray-950/95 border border-white/15 rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="text-gray-400 font-semibold mb-1">{label}</p>
      <p className="text-white">
        Nifty: <span className="font-mono">{fmtN(row.nifty_close, 2)}</span>
        {row.nifty_change_pct != null && (
          <span className={`ml-2 ${row.nifty_change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {pct(row.nifty_change_pct)}
          </span>
        )}
      </p>
      <p className={`${row.fii_net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
        FII net: <span className="font-mono">₹{fmtN(row.fii_net)} cr</span>
      </p>
      {row.dii_net != null && (
        <p className={`${row.dii_net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          DII net: <span className="font-mono">₹{fmtN(row.dii_net)} cr</span>
        </p>
      )}
    </div>
  );
};

const HistoricalTrendChart = ({ days = 20 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/market-snapshot/history/trend?days=${days}`);
        const j = await r.json();
        if (!cancelled) setRows(j.rows || []);
      } catch (_e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const chartData = useMemo(() => rows.map((r) => ({
    ...r,
    label: fmtDate(r.date),
  })), [rows]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const first = rows.find((r) => r.nifty_close != null);
    const last = [...rows].reverse().find((r) => r.nifty_close != null);
    const niftyRet = first && last ? ((last.nifty_close - first.nifty_close) / first.nifty_close) * 100 : null;

    const fiiVals = rows.map((r) => r.fii_net).filter((v) => v != null);
    const fiiCum = fiiVals.reduce((a, b) => a + b, 0);
    const fiiBuyDays = fiiVals.filter((v) => v > 0).length;
    const fiiSellDays = fiiVals.filter((v) => v < 0).length;

    // "Trend read" — simple momentum sign
    const trend = niftyRet == null ? 'Flat' : niftyRet > 1 ? 'Uptrend' : niftyRet < -1 ? 'Downtrend' : 'Sideways';

    // FII correlation with Nifty change% — Pearson r
    const paired = rows
      .map((r) => (r.nifty_change_pct != null && r.fii_net != null ? [r.nifty_change_pct, r.fii_net] : null))
      .filter(Boolean);
    let corr = null;
    if (paired.length >= 5) {
      const n = paired.length;
      const mx = paired.reduce((a, [x]) => a + x, 0) / n;
      const my = paired.reduce((a, [, y]) => a + y, 0) / n;
      let num = 0, dx2 = 0, dy2 = 0;
      paired.forEach(([x, y]) => {
        num += (x - mx) * (y - my);
        dx2 += (x - mx) ** 2;
        dy2 += (y - my) ** 2;
      });
      const den = Math.sqrt(dx2 * dy2);
      corr = den ? num / den : 0;
    }

    return { niftyRet, fiiCum, fiiBuyDays, fiiSellDays, trend, corr, count: rows.length };
  }, [rows]);

  const niftyClose = chartData.map((r) => r.nifty_close).filter((v) => v != null);
  const niftyDomain = niftyClose.length
    ? [Math.floor(Math.min(...niftyClose) * 0.995), Math.ceil(Math.max(...niftyClose) * 1.005)]
    : ['auto', 'auto'];

  return (
    <section className="mb-6" data-testid="section-historical-trend">
      <div className="bg-gradient-to-b from-white/[0.03] to-black/40 border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <LineIcon className="w-4 h-4 text-golden" />
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Historical Trend — Last {stats?.count || days} Sessions
            </h3>
          </div>
          {stats && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400">Trend:</span>
              <span className={`px-2 py-0.5 rounded font-semibold ${
                stats.trend === 'Uptrend' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                : stats.trend === 'Downtrend' ? 'bg-red-500/15 text-red-300 border border-red-500/30'
                : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
              }`} data-testid="trend-badge">{stats.trend}</span>
            </div>
          )}
        </div>

        {/* Stat strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="trend-stats">
            <StatCard
              icon={stats.niftyRet >= 0 ? TrendingUp : TrendingDown}
              label={`${stats.count}D Nifty Return`}
              value={pct(stats.niftyRet)}
              tone={stats.niftyRet >= 0 ? 'positive' : 'negative'}
            />
            <StatCard
              icon={Wallet}
              label={`FII Net (${stats.count}D)`}
              value={`₹${fmtN(stats.fiiCum)} cr`}
              tone={stats.fiiCum >= 0 ? 'positive' : 'negative'}
              sub={stats.fiiCum >= 0 ? 'Net buyers' : 'Net sellers'}
            />
            <StatCard
              label="FII Buy/Sell Days"
              value={`${stats.fiiBuyDays} / ${stats.fiiSellDays}`}
              sub={stats.fiiBuyDays > stats.fiiSellDays ? 'Buying dominant' : stats.fiiBuyDays < stats.fiiSellDays ? 'Selling dominant' : 'Balanced'}
              tone={stats.fiiBuyDays > stats.fiiSellDays ? 'positive' : stats.fiiBuyDays < stats.fiiSellDays ? 'negative' : 'default'}
            />
            <StatCard
              label="FII ↔ Nifty Correlation"
              value={stats.corr == null ? '—' : stats.corr.toFixed(2)}
              sub={
                stats.corr == null ? 'insufficient data'
                : stats.corr > 0.4 ? 'Strong: FII drives price'
                : stats.corr > 0.1 ? 'Moderate positive'
                : stats.corr < -0.4 ? 'Inverse: contra flow'
                : 'Weak / noisy'
              }
              tone={stats.corr == null ? 'default' : stats.corr > 0.4 ? 'positive' : stats.corr < -0.4 ? 'warning' : 'default'}
            />
          </div>
        )}

        {/* Chart */}
        <div className="w-full h-72 md:h-80" data-testid="trend-chart">
          {loading ? (
            <div className="h-full flex items-center justify-center text-gray-500 text-sm">Loading trend…</div>
          ) : chartData.length < 2 ? (
            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
              Not enough snapshots yet. The chart populates as daily snapshots accrue.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="niftyStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#FCD34D" />
                    <stop offset="100%" stopColor="#F59E0B" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#9CA3AF', fontSize: 10 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  domain={['auto', 'auto']}
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                  tickLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
                  label={{ value: 'FII ₹ cr', angle: -90, position: 'insideLeft', fill: '#6B7280', fontSize: 10, dy: 30 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={niftyDomain}
                  tick={{ fill: '#FBBF24', fontSize: 10 }}
                  axisLine={{ stroke: 'rgba(251,191,36,0.2)' }}
                  tickLine={false}
                  label={{ value: 'Nifty', angle: 90, position: 'insideRight', fill: '#FBBF24', fontSize: 10, dy: -30 }}
                />
                <ReferenceLine y={0} yAxisId="left" stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                <Tooltip content={<TooltipCard />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar yAxisId="left" dataKey="fii_net" name="FII net (₹ cr)" barSize={14} radius={[3, 3, 0, 0]}>
                  {chartData.map((r, i) => (
                    <Cell key={i} fill={r.fii_net >= 0 ? 'rgba(16,185,129,0.7)' : 'rgba(239,68,68,0.7)'} />
                  ))}
                </Bar>
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="nifty_close"
                  name="Nifty close"
                  stroke="url(#niftyStroke)"
                  strokeWidth={2.5}
                  dot={{ fill: '#FBBF24', r: 3, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#FCD34D' }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        <p className="text-[10px] text-gray-500 mt-3 italic">
          Bars = daily FII net cash-market flow (green = buying, red = selling). Line = Nifty 50 closing level.
          Correlation is a rough signal — not a leading indicator.
        </p>
      </div>
    </section>
  );
};

export default HistoricalTrendChart;
